<template>
  <div class="internal-fields">
    <!-- Info hint -->
    <info-hint>Fields your staff fill out when creating bookings internally. Independent from the public booking site.</info-hint>

    <!-- Loading skeleton -->
    <div v-if="loading" class="internal-fields__skeleton">
      <div v-for="n in 2" :key="'skel-if-' + n" class="internal-fields__skel-row">
        <span class="skel skel--dot mr-2"></span>
        <span class="skel skel--text flex-grow-1"></span>
        <span class="skel skel--switch mr-2"></span>
        <span class="skel skel--btn"></span>
      </div>
    </div>

    <!-- Empty: library has no fields -->
    <div v-else-if="libraryIsEmpty" class="internal-fields__empty">
      <p class="mb-2 text-white">You haven't created any custom fields yet.</p>
      <router-link to="/settings/custom-fields" class="internal-fields__link">
        <i class="tim-icons el-icon-plus mr-1"></i> Create your first custom field
      </router-link>
    </div>

    <!-- Empty: library has fields, none attached -->
    <div v-else-if="!attached.length" class="internal-fields__empty">
      <p class="mb-0 text-white">No fields added yet.</p>
    </div>

    <!-- Attached list -->
    <draggable
      v-else
      :value="attached"
      handle=".internal-fields__drag"
      :animation="150"
      @input="onReorder"
    >
      <div
        v-for="(f, idx) in attached"
        :key="rowKey(f, idx)"
        class="internal-fields__row"
      >
        <div class="internal-fields__top">
          <span class="internal-fields__drag" tabindex="0" role="button" aria-label="Drag to reorder">
            <i class="el-icon-rank"></i>
          </span>

          <i class="internal-fields__type-icon" :class="typeIconClass(f.type_id)"></i>

          <div class="internal-fields__info">
            <div class="internal-fields__label text-white">{{ f.label }}</div>
            <div class="internal-fields__meta">{{ typeName(f.type_id) }}</div>
          </div>

          <el-tooltip content="Remove" effect="light" :open-delay="300" placement="top">
            <base-button class="internal-fields__remove-btn" simple type="danger" link size="sm" @click="removeField(idx)">
              <i class="tim-icons icon-simple-remove"></i>
            </base-button>
          </el-tooltip>
        </div>

        <div class="internal-fields__controls">
          <label class="internal-fields__required mb-0">
            <el-switch
              active-color="#e14eca"
              :value="!!f.required"
              @change="val => toggleRequired(idx, val)"
            />
            <span class="internal-fields__required-label">Required</span>
          </label>

          <el-tooltip content="Show alert on booking calendar when filled out" effect="light" :open-delay="300" placement="top">
            <label class="internal-fields__required mb-0">
              <el-switch
                active-color="#e14eca"
                :value="!!f.add_alert"
                @change="val => toggleAlert(idx, val)"
              />
              <span class="internal-fields__required-label">Creates alert</span>
            </label>
          </el-tooltip>
        </div>
      </div>
    </draggable>

    <!-- Picker -->
    <div v-if="!libraryIsEmpty && !loading" class="internal-fields__picker mt-3">
      <label class="internal-fields__picker-label">Add a field</label>
      <el-select
        v-model="pickerValue"
        class="select-primary"
        placeholder="Select from library..."
        filterable
        :popper-append-to-body="false"
        popper-class="internal-fields__picker-popper"
        @change="onPick"
      >
        <el-option
          v-for="f in availableFields"
          :key="'AF' + f.id"
          class="select-primary"
          :label="f.label"
          :value="f.id"
        >
          <div class="internal-fields__option">
            <span class="internal-fields__option-label">{{ f.label }}</span>
            <span class="internal-fields__option-type">{{ typeName(f.type_id) }}</span>
          </div>
        </el-option>
      </el-select>
      <div v-if="!availableFields.length" class="internal-fields__picker-hint">
        All library fields are attached.
        <router-link to="/settings/custom-fields">Create another <span aria-hidden="true">→</span></router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { Select, Option, Switch, Tooltip } from 'element-ui'
import draggable from 'vuedraggable'

const TYPE_META = {
  1: { name: 'Checkbox',    icon: 'far fa-check-square' },
  2: { name: 'Input field', icon: 'tim-icons icon-pencil' },
  3: { name: 'Dropdown',    icon: 'tim-icons icon-bullet-list-67' },
}

export default {
  name: 'InternalBookingFields',
  components: {
    draggable,
    [Select.name]: Select,
    [Option.name]: Option,
    [Switch.name]: Switch,
    [Tooltip.name]: Tooltip,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    libraryFields: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pickerValue: null,
    }
  },
  computed: {
    attached() {
      return this.value || []
    },
    libraryIsEmpty() {
      return !this.libraryFields || this.libraryFields.length === 0
    },
    availableFields() {
      const attachedIds = new Set(this.attached.map(f => f.custom_field_id))
      return this.libraryFields.filter(f => !attachedIds.has(f.id))
    },
  },
  methods: {
    typeName(id) {
      return (TYPE_META[id] || {}).name || ''
    },
    typeIconClass(id) {
      return (TYPE_META[id] || {}).icon || ''
    },
    rowKey(f, idx) {
      return f.id ? 'A' + f.id : 'N' + f.custom_field_id + '_' + idx
    },
    emitChange(next) {
      const withPositions = next.map((f, i) => ({ ...f, position: i }))
      this.$emit('input', withPositions)
    },
    onPick(customFieldId) {
      if (!customFieldId) return
      const libField = this.libraryFields.find(f => f.id === customFieldId)
      if (!libField) return
      this.pickerValue = null
      const next = [
        ...this.attached,
        {
          id: null,
          custom_field_id: customFieldId,
          label: libField.label,
          type_id: libField.type_id,
          required: 0,
          add_alert: libField.add_alert ? 1 : 0,
          position: this.attached.length,
        },
      ]
      this.emitChange(next)
    },
    toggleRequired(idx, val) {
      const next = this.attached.map((f, i) => (
        i === idx ? { ...f, required: val ? 1 : 0 } : f
      ))
      this.emitChange(next)
    },
    toggleAlert(idx, val) {
      const next = this.attached.map((f, i) => (
        i === idx ? { ...f, add_alert: val ? 1 : 0 } : f
      ))
      this.emitChange(next)
    },
    removeField(idx) {
      const next = this.attached.filter((_, i) => i !== idx)
      this.emitChange(next)
    },
    onReorder(next) {
      this.emitChange(next)
    },
  },
}
</script>

<style scoped>
.internal-fields {
  display: block;
}

.internal-fields__skeleton {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.internal-fields__skel-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 56px;
  padding: 10px 12px;
  background: rgba(128, 128, 128, 0.05);
  border-left: 3px solid rgba(225, 78, 202, 0.3);
  border-radius: 0.375rem;
}

.internal-fields__empty {
  text-align: center;
  padding: 1.25rem 1rem;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  background: rgba(128, 128, 128, 0.05);
  border: 1px dashed rgba(255, 255, 255, 0.08);
  border-radius: 0.375rem;
}

.internal-fields__link {
  display: inline-flex;
  align-items: center;
  color: #e14eca;
  font-size: 13px;
  font-weight: 500;
}
.internal-fields__link:hover {
  text-decoration: underline;
  color: #e14eca;
}

.internal-fields__row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  background: rgba(128, 128, 128, 0.05);
  border-left: 3px solid #e14eca;
  border-radius: 0.375rem;
  margin-bottom: 6px;
  transition: background 0.15s ease;
}
.internal-fields__row:hover {
  background: rgba(128, 128, 128, 0.12);
}

.internal-fields__top {
  display: flex;
  align-items: center;
  gap: 12px;
}

.internal-fields__drag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
  margin: -8px -4px -8px -8px;
  padding: 8px;
  cursor: grab;
  color: rgba(255, 255, 255, 0.4);
  font-size: 16px;
  flex-shrink: 0;
  touch-action: none;
  border-radius: 4px;
}
.internal-fields__drag:active {
  cursor: grabbing;
}
.internal-fields__drag:focus-visible {
  outline: 2px solid #e14eca;
  outline-offset: 2px;
}

.internal-fields__type-icon {
  color: #e14eca;
  font-size: 14px;
  width: 18px;
  text-align: center;
  flex-shrink: 0;
}

.internal-fields__info {
  flex: 1;
  min-width: 0;
}

.internal-fields__label {
  font-size: 0.8125rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.internal-fields__meta {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.45);
  font-weight: 600;
  margin-top: 2px;
}

.internal-fields__controls {
  display: flex;
  align-items: center;
  gap: 20px;
  padding-left: 50px;
  flex-wrap: wrap;
}

.internal-fields__required {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.internal-fields__required-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
}

.internal-fields__required ::v-deep .el-switch__core {
  height: 16px;
  width: 32px !important;
  border-radius: 8px;
}
.internal-fields__required ::v-deep .el-switch__core::after {
  width: 12px;
  height: 12px;
}
.internal-fields__required ::v-deep .el-switch.is-checked .el-switch__core::after {
  margin-left: -13px;
}

.internal-fields__remove-btn {
  min-width: 32px;
  min-height: 32px;
  padding: 6px;
  margin-left: auto;
}

.internal-fields__picker {
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.internal-fields__picker-label {
  display: block;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 6px;
}

.internal-fields__picker ::v-deep .el-select {
  width: 100%;
}

.internal-fields__picker-hint {
  margin-top: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}
.internal-fields__picker-hint a {
  color: #e14eca;
}

.internal-fields__option-type {
  font-size: 10px;
  text-transform: uppercase;
  color: rgba(125, 125, 125, 0.8);
  letter-spacing: 0.04em;
}

@media (max-width: 575.98px) {
  .internal-fields__row {
    gap: 8px;
    padding: 10px;
  }
  .internal-fields__controls {
    gap: 14px;
    padding-left: 38px;
  }
}
</style>

<style>
.internal-fields__picker-popper .el-select-dropdown__item {
  height: auto;
  min-height: 34px;
  line-height: 1.4;
  padding: 8px 20px;
  white-space: normal;
}
.internal-fields__picker-popper .internal-fields__option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.internal-fields__picker-popper .internal-fields__option-label {
  flex: 1;
  white-space: normal;
  word-break: break-word;
}
.internal-fields__picker-popper .internal-fields__option-type {
  flex-shrink: 0;
  padding-top: 2px;
}

.white-content .internal-fields__skel-row {
  background: rgba(0, 0, 0, 0.02);
  border-left-color: rgba(225, 78, 202, 0.2);
}
.white-content .internal-fields__empty {
  background: rgba(0, 0, 0, 0.02);
  border-color: rgba(0, 0, 0, 0.15);
  color: rgba(0, 0, 0, 0.55);
}
.white-content .internal-fields__row {
  background: rgba(0, 0, 0, 0.02);
  border-left-color: #cc5fad;
}
.white-content .internal-fields__row:hover {
  background: rgba(0, 0, 0, 0.05);
}
.white-content .internal-fields__type-icon,
.white-content .internal-fields__link,
.white-content .internal-fields__link:hover,
.white-content .internal-fields__picker-hint a {
  color: #cc5fad;
}
.white-content .internal-fields__option-type {
  color: rgba(90, 90, 90, 0.9);
}
.white-content .internal-fields__drag {
  color: rgba(0, 0, 0, 0.35);
}
.white-content .internal-fields__meta {
  color: rgba(0, 0, 0, 0.45);
}
.white-content .internal-fields__required-label {
  color: rgba(0, 0, 0, 0.55);
}
.white-content .internal-fields__picker {
  border-top-color: rgba(0, 0, 0, 0.08);
}
.white-content .internal-fields__picker-label {
  color: rgba(0, 0, 0, 0.55);
}
.white-content .internal-fields__picker-hint {
  color: rgba(0, 0, 0, 0.45);
}
</style>
